import {Box, Button, Grid, MenuItem, Paper, TextField, Typography, Accordion, AccordionSummary, AccordionDetails} from '@mui/material'
import axios from 'axios'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { MoonLoader } from 'react-spinners'
import DynamicForm from '../../../../reusable-components/forms/DynamicForm'
import FormWrapper from '../../../../reusable-components/Wrapper/Wrapper'
import { EDIT_BEHAVIOUR_NOTE_FORM_INPUTS } from '../../../Notes/Behaviour_Notes/BEHAVIOUR_CONSTANTS'
import { RootState } from '../../../../../setup/redux/RootReducer'
import FrequencyDateRangePicker from '../../../../reusable-components/NoteType/NoteType'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const selectTimeZone = (state: RootState) => state.timeZone.timeZone

export default function BehaviourAssesmentNoteForm() {
  const params: {id: string} = useParams()
  const history = useHistory()
  const [isFormLoading, setIsFormLoading] = useState(true)
  const [formData, setFormData] = useState<any>({})
  const authUser = useSelector((state: {auth: any}) => state.auth)
  const [uiElements, setUiElements] = useState([])
  const [clientId, setClientId] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<{ label: string; value: string } | null>(null)
  const timeZone = useSelector(selectTimeZone)
  const [behaviorFormData, setBehaviorFormData] = useState([])
  
useEffect(() => {
    const loadData = async () => {
      setIsFormLoading(true)
      try {
        // Fetch the progress note data (now includes client information)
        const res = await axios.get(`/progressNotes/getById/${params.id}`);
        const data = res?.data?.result;
        
        // Timezone-converted dates
        const formattedStartDate = dayjs.utc(data.startDate).tz(timeZone, true);
        const formattedEndDate = dayjs.utc(data.endDate).tz(timeZone, true);
        const formattedNextAppointment = dayjs.utc(data.nextAppointment).tz(timeZone, true);
        
        // Create client option using clientFullName from the response
        const clientOption = {
          label: data.clientFullName, // This is now included in the response
          value: data.clientId
        };
        
        // Create the updated clients array with just the current client
        const updatedClients = [clientOption];
        const selectedClient = clientOption;
        
        // Set basic form data
        const prevFormData = {
          startDate:formattedStartDate,
          endDate:formattedEndDate,
          timeIn:data.timeIn || '',
          timeOut:data.timeOut || '',
          noteType:data.noteType || '',
          dangerTo: data?.dangerTo || [],
          status: data?.status || '',
          responseToTx: data?.responseToTx || '',
          appearance: data?.appearance || '',
          judgment: data?.judgment || '',
          insights: data?.insights || '',
          speech: data?.speech || '',
          mood: data?.mood || '',
          behaviour: data?.behaviour || '',
          thoughtContent: data?.thoughtContent || '',
          thoughtProcess: data?.thoughtProcess || '',
          orientation: data?.orientation || '',
          affect: data?.affect || '',
          therapeuticInterventions: data?.therapeuticInterventions || [],
          location: data?.historyOfIllness?.location || '',
          duration: data?.historyOfIllness?.duration || '',
          timing: data?.historyOfIllness?.timing || '',
          associatedSymptoms: data?.historyOfIllness?.associatedSymptoms || '',
          medicalHx: data?.historyOfIllness?.medicalHx || '',
          goalsObjectives: data?.goalsObjectives || '',
          evidenceOfResponse: data?.evidenceOfResponse || '',
          interventionStudies: data?.interventionStudies || '',
          sessionContent: data?.sessionContent || '',
          futureTreatment: data?.futureTreatment || '',
          assignedHomework: data?.assignedHomework || '',
          nextAppointment: formattedNextAppointment,           
        }
  
        // Load behavior mapping data if it exists
        if (data?.behaviorMapping) {
          const behaviorsData = data.behaviorMapping.map((behavior) => {
            return {
              isEdit: false,
              id: behavior._id,
              targetBehavior: behavior.targetBehavior,
              targetBehaviorId: behavior.targetBehaviorId,
              targetRating: behavior.targetRating || '',
              targetDescription: behavior.targetDescription || '',
              replacementBehavior: behavior.replacementBehavior.map((replacement) => ({
                _id: replacement._id,
                name: replacement.name,
                type: replacement.type,
                rating: replacement.rating || '',
                description: replacement.description || ''
              })),
            }
          })
          setBehaviorFormData(behaviorsData)
        }
        
        setFormData(prevFormData)
        setClientId(data?.clientId)
        
        // Updated mapping to disable all fields
        const updatedFormData = EDIT_BEHAVIOUR_NOTE_FORM_INPUTS.map((item) => {
          if (item.name === 'clientId') {
            return {
              ...item,
              options: updatedClients,
              disabled: true,
              value: selectedClient.value,
            }
          }
          // Disable all other fields
          return {
            ...item,
            disabled: true
          }
        })
        
        setSelectedClientId(selectedClient)
        setUiElements(updatedFormData)
      } catch (error) {
        console.error('Error loading progress note data:', error)
      } finally {
        setIsFormLoading(false)
      }
    }
  
    loadData()
  }, [])
 
  const buttons = [
    {label: 'Cancel', variant: 'outlined', type: 'button'},
  ]
  return (
      <FormWrapper
        title='Deleted Behavior Note'
        content={
          <>  
          {isFormLoading ? (
                <div
                  className='overlay d-flex align-middle justify-content-center align-middle'
                  style={{marginTop: '100px'}}
                >
                  <MoonLoader color='#9db2fc' size={60} loading={isFormLoading} />
                </div>
              ) : (
                <>          
                <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  marginLeft: '1rem',
                }}
                  color='primary'
                >
                  Note Type
                </Typography> 
                <FrequencyDateRangePicker
                  isEntry = {false}
                  isTemplate = {true}
                  isFullForm = {true}
                  isEdit = {true}
                  formData={formData}
                  setFormData={setFormData}
              />

              {formData?.noteType === "DAILY" && behaviorFormData.map((behavior, index) => (
            <Accordion key={behavior.id} sx={{ mb: 2, mt: 2, border: 'none', width: '97%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                  '&:hover': {
                    backgroundColor: '#eeeeee',
                  },
                }}
              >
                <Typography variant='h6' sx={{ fontWeight: 600, color: '#3f51b5' }}>
                  Behavior {index + 1}: {behavior.targetBehavior}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={8} sm={8} md={8}>
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        backgroundColor: '#f7f7f7',
                      }}
                    >
                      <Grid container alignItems="center">
                        <Grid item>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: '#3f51b5',
                            }}
                          >
                            Target Behavior
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="body1"
                            sx={{
                              color: '#333',
                              fontStyle: 'italic',
                              p: 1,
                              borderRadius: 1,
                              marginLeft: "7rem"
                            }}
                          >
                            {behavior.targetBehavior}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4}>
                    <TextField
                      fullWidth
                      select
                      label='Rating'
                      name='targetRating'
                      variant='outlined'
                      value={behavior.targetRating || ''}
                      onChange={() => {}}
                    >
                      <MenuItem value="0">0</MenuItem>
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                      <MenuItem value="4">4</MenuItem>
                      <MenuItem value="5">5</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Description'
                      name='targetDescription'
                      variant='outlined'
                      multiline
                      rows={3}
                      value={behavior.targetDescription}
                      onChange={() => {}}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {behavior.replacementBehavior.map((replacement, replacementIndex) => (
                      <Box key={replacementIndex} sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={8} sm={8} md={8}>
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                backgroundColor: '#f7f7f7',
                              }}
                            >
                              <Grid container alignItems="center">
                                <Grid item>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#3f51b5',
                                    }}
                                  >
                                    Replacement Behavior
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#333',
                                      fontStyle: 'italic',
                                      p: 1,
                                      borderRadius: 1,
                                      marginLeft: "4rem"
                                    }}
                                  >
                                    {replacement.name}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>
                          <Grid item xs={4} sm={4} md={4}>
                            <TextField
                              fullWidth
                              select
                              label='Rating'
                              name='rating'
                              variant='outlined'
                              value={replacement.rating || ''}
                              onChange={() => {}}
                            >
                              <MenuItem value="0">0</MenuItem>
                              <MenuItem value="1">1</MenuItem>
                              <MenuItem value="2">2</MenuItem>
                              <MenuItem value="3">3</MenuItem>
                              <MenuItem value="4">4</MenuItem>
                              <MenuItem value="5">5</MenuItem>
                            </TextField>
                          </Grid>
                        </Grid>
                        <TextField
                          fullWidth
                          label='Description'
                          name='description'
                          variant='outlined'
                          multiline
                          rows={3}
                          value={replacement.description}
                          onChange={() => {}}
                        />
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
              ))}

            <DynamicForm
              fields={uiElements}
              data={formData}
              onSubmit={() => {}}
              buttons={buttons}
            />
            </>
          )}
          </>
        }
        />
  )
}
